/**
 * Last-viewed casino resume after login.
 *
 * Source of truth: existing `app.persistedState.casinoSchema` (loadState/saveState
 * via localStorage `appState`). No new localStorage key, no server field.
 *
 * sessionStorage flag marks a post-login visit to ChooseCasinos so we auto-enter
 * once. Side-menu / header "Select Casino" use forceSelect and clear the flag.
 */

import { canEnterCasino } from './casinoComingSoonAccess';
import { clearRandomOutcomesSession } from './loginCasinoReveal';

export const RESUME_LAST_VIEWED_CASINO_KEY = 'slotcheck.resumeLastViewedCasino';

/** Query/location flag: stay on Select Casino; do not auto-open last viewed. */
export const FORCE_SELECT_CASINO_QUERY = 'forceSelect';

export type LastVisitCasino = {
  casinoId?: number | string | null;
  isactive?: boolean;
  isComingSoon?: boolean;
  isPreview?: boolean;
};

export type ResumeAction =
  | { type: 'wait' }
  | { type: 'skip' }
  | { type: 'enter'; casinoId: number }
  | { type: 'give_up' };

function defaultSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Coerce casino ids so string "12" from JSON matches number 12 from the API. */
export function normalizeCasinoId(
  casinoId: number | string | null | undefined
): number | null {
  if (casinoId == null || casinoId === '') return null;
  const n = typeof casinoId === 'number' ? casinoId : Number(casinoId);
  return Number.isFinite(n) ? n : null;
}

/**
 * Mark that the next ChooseCasinos mount should try to auto-enter last viewed.
 * Every sign-in path calls this (SignIn, CasinoSignIn, Emulate, UserConfirm), so it
 * also starts a new Random Outcomes session: the first casino entry of this login
 * shows Random Outcomes again, even if it was accepted in an earlier login.
 */
export function markResumeLastViewedCasino(
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.setItem(RESUME_LAST_VIEWED_CASINO_KEY, '1');
  } catch {
    // private mode / blocked storage — skip resume
  }
  clearRandomOutcomesSession(storage);
}

/** True when resume is marked; does not clear (peek). */
export function peekResumeLastViewedCasino(
  storage: Storage | null = defaultSessionStorage()
): boolean {
  try {
    if (!storage) return false;
    return storage.getItem(RESUME_LAST_VIEWED_CASINO_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Read and clear the resume flag. Returns true only once per mark.
 * Missing / blocked storage → false (show selection).
 */
export function consumeResumeLastViewedCasino(
  storage: Storage | null = defaultSessionStorage()
): boolean {
  try {
    if (!storage) return false;
    const marked = storage.getItem(RESUME_LAST_VIEWED_CASINO_KEY) === '1';
    storage.removeItem(RESUME_LAST_VIEWED_CASINO_KEY);
    return marked;
  } catch {
    return false;
  }
}

/** Clear resume flag without consuming (forceSelect / tests). */
export function clearResumeLastViewedCasinoFlag(
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    storage?.removeItem(RESUME_LAST_VIEWED_CASINO_KEY);
  } catch {
    // ignore
  }
}

export function isForceSelectCasinoRequest(
  search: string | null | undefined,
  state?: { forceSelect?: boolean } | null
): boolean {
  if (state?.forceSelect === true) return true;
  if (!search) return false;
  try {
    const params = new URLSearchParams(
      search.startsWith('?') ? search : `?${search}`
    );
    const v = params.get(FORCE_SELECT_CASINO_QUERY);
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

/**
 * True when the persisted casinoSchema is still enterable for this user:
 * present in the accessible catalog, active, and not a locked Coming Soon.
 */
export function isValidLastViewedCasino(
  casinoId: number | string | null | undefined,
  accessibleCasinos: LastVisitCasino[] | null | undefined
): boolean {
  const id = normalizeCasinoId(casinoId);
  if (id == null || !accessibleCasinos?.length) return false;
  const match = accessibleCasinos.find((c) => normalizeCasinoId(c?.casinoId) === id);
  if (!match) return false;
  if (match.isactive === false) return false;
  return canEnterCasino(match);
}

/**
 * Pick resume target from persisted casinoSchema id + accessible catalog.
 * Returns null → show Select Casino.
 */
export function pickResumeCasinoId(
  persistedCasinoId: number | string | null | undefined,
  accessibleCasinos: LastVisitCasino[] | null | undefined
): number | null {
  const id = normalizeCasinoId(persistedCasinoId);
  if (id == null) return null;
  return isValidLastViewedCasino(id, accessibleCasinos) ? id : null;
}

/**
 * Pure resume decision for ChooseCasinos.
 *
 * Critical: must wait until the casino catalog has finished loading before
 * consuming the session flag. Consuming against an empty catalog (the old
 * race when `isLoading` started false) permanently skips auto-open.
 *
 * The Random Outcomes disclaimer no longer gates the resume: the last casino is
 * opened as soon as the catalog is ready and Home shows the disclaimer as a
 * locked drawer over it (lib/loginCasinoReveal). forceSelect still always skips.
 */
export function resolveLastViewedResume(input: {
  forceSelect: boolean;
  catalogLoaded: boolean;
  isLoading: boolean;
  resumeFlagPresent: boolean;
  persistedCasinoId: number | string | null | undefined;
  accessibleCasinos: LastVisitCasino[] | null | undefined;
}): ResumeAction {
  // forceSelect (side menu / header logo) must never auto-open — even if the
  // Random Outcomes disclaimer is shown and then accepted on that visit.
  if (input.forceSelect) return { type: 'skip' };
  if (!input.catalogLoaded || input.isLoading) return { type: 'wait' };
  if (!input.resumeFlagPresent) return { type: 'skip' };

  const resumeId = pickResumeCasinoId(
    input.persistedCasinoId,
    input.accessibleCasinos
  );
  if (resumeId == null) return { type: 'give_up' };
  return { type: 'enter', casinoId: resumeId };
}
